import { Button } from "./ui/button";
import { Home, Bot, Users, Phone, BookOpen, ShoppingCart, Sparkles, UserSquare2, Package, LifeBuoy, PlayCircle } from "lucide-react";
import { NavLink } from "./NavLink";
import TrustRibbon from "./TrustRibbon";
import neilLogo from "@/assets/neil-ai-logo-new.png";
import { Link } from "react-router-dom";
import CartIcon from "./CartIcon";

const Navigation = () => {
  const navLinks = [
    { to: "/", icon: Home, label: "Home" },
    { to: "/nexora", icon: Sparkles, label: "NEXORA", highlight: true },
    { to: "/robots", icon: Bot, label: "Robotics" },
    { to: "/accessories", icon: Package, label: "Accessories" },
    { to: "/ai-automation", icon: Sparkles, label: "AI Automation" },
    { to: "/case-studies", icon: Users, label: "Case Studies" },
    { to: "/team", icon: UserSquare2, label: "Team" },
    { to: "/support", icon: LifeBuoy, label: "Support" },
    { to: "/videos", icon: PlayCircle, label: "Videos" },
    { to: "/blog", icon: BookOpen, label: "Blog" },
  ] as const;

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <TrustRibbon />

      <nav
        aria-label="Primary"
        className="border-b border-[hsl(var(--line))] backdrop-blur-xl bg-background/85"
      >
        <div className="max-w-7xl mx-auto px-3 md:px-5 h-16 md:h-[72px] flex items-center justify-between gap-3 lg:gap-5">
          <Link to="/" aria-label="Neil AI Global Solutions home" className="flex items-center min-w-0 shrink-0 group">
            <img
              src={neilLogo}
              alt="Neil AI Global Solutions"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>

          <div className="hidden md:flex items-center justify-center flex-1 gap-0.5 lg:gap-1 min-w-0">
            {navLinks.map((link) => {
              const Icon = link.icon;
              const highlight = "highlight" in link && link.highlight;
              return (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={`flex items-center gap-1.5 px-2.5 lg:px-3 h-10 rounded-full text-[12px] font-medium transition-colors whitespace-nowrap ${
                    highlight
                      ? "text-[#00A3FF] bg-[#00A3FF]/10 border border-[#00A3FF]/30 hover:bg-[#00A3FF]/20 font-semibold"
                      : "text-muted-foreground hover:text-foreground hover:bg-primary/5"
                  }`}
                  activeClassName={highlight ? "bg-[#00A3FF]/25 text-white" : "text-primary bg-primary/10"}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{link.label}</span>
                </NavLink>
              );
            })}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <CartIcon />
            <Button
              asChild
              size="sm"
              variant="outline"
              className="hidden lg:inline-flex h-10 px-3 rounded-full border-[#FFD700]/40 bg-[#FFD700]/5"
              title="24/7 Customer Support"
            >
              <a href="tel:+13362033184" className="inline-flex items-center gap-2">
                <LifeBuoy className="h-4 w-4 text-[#FFD700]" />
                <span className="text-[11px] font-semibold">24/7 (336) 203-3184</span>
              </a>
            </Button>
            <Button
              asChild
              size="sm"
              variant="outline"
              className="hidden sm:inline-flex h-10 px-4 rounded-full border-primary/35 bg-background/50"
            >
              <a href="tel:3369624539" className="inline-flex items-center gap-2">
                <Phone className="h-4 w-4" />
                <span>(336) 962-4539</span>
              </a>
            </Button>

            <Button
              asChild
              size="sm"
              className="shrink-0 h-10 px-4 text-[12px] font-semibold tracking-wide rounded-full bg-gradient-to-r from-[hsl(43_75%_55%)] to-[hsl(43_85%_65%)] text-black hover:opacity-95 shadow-[0_4px_20px_hsl(43_75%_55%/0.35)]"
            >
              <a
                href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics"
                target="_blank"
                rel="noopener noreferrer"
              >
                Book Demo
              </a>
            </Button>
          </div>
        </div>

        <div className="md:hidden border-t border-[hsl(var(--line))] px-2 py-1.5 flex items-center gap-1 overflow-x-auto no-scrollbar">
          {[...navLinks, { to: "tel:3369624539", icon: Phone, label: "Call" }].map((link) => {
            const Icon = link.icon;
            const highlight = "highlight" in link && (link as any).highlight;
            if (highlight) {
              return (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className="flex items-center gap-1 h-8 px-2.5 rounded-full text-[11px] font-semibold text-[#00A3FF] bg-[#00A3FF]/15 border border-[#00A3FF]/40 shrink-0"
                  activeClassName="bg-[#00A3FF]/30 text-white"
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{link.label}</span>
                </NavLink>
              );
            }
            return (
              <NavLink
                key={link.to}
                to={link.to}
                aria-label={link.label}
                className="flex items-center justify-center h-8 w-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-primary/5 shrink-0"
                activeClassName="text-primary bg-primary/10"
              >
                <Icon className="h-3.5 w-3.5" />
              </NavLink>
            );
          })}
        </div>
      </nav>
    </header>
  );
};

export default Navigation;
